import { useState } from "react";
import { useTranslate } from "@refinedev/core";
import { Tabs } from "@fawkes/ui";

import { AnimalPresentView } from "./AnimalPresentView";
import { AnimalPlacedView } from "./AnimalPlacedView";
import { EntingenOverzicht } from "./EntingenOverzicht";
import { OntwormenOverzicht } from "./OntwormenOverzicht";
import { MedicijnOverzicht } from "./MedicijnOverzicht";

import "./OpvangOverzichten.css";

// sidebar-menu-parity: the SPA port of the Jmix `OpvangOverzichten.view` (@Route("opvang-overzichten")).
// The Jmix redesign consolidated the previously-separate opvang overview screens under a single
// "Opvangoverzichten" menu item with tabs (the same treatment as "Pleeggezin overzichten"). This mirrors
// that: one page, the five Jmix tabs in order, each rendering the already-migrated React overview that
// used to be its own top-level menu item:
//   • Aanwezig               — AnimalPresentView   (dier-aanwezig)
//   • Entingen               — EntingenOverzicht   (entingen-overzicht)
//   • Ontwormen & ontvlooien — OntwormenOverzicht  (ontwormen-overzicht)
//   • Medicijn               — MedicijnOverzicht   (medicijn-overzicht)
//   • Vertrokken             — AnimalPlacedView    (dier-placements — the "Vertrokken op …" overview)
// The active tab is rendered on demand (switching remounts it) — the api feeds are cheap and this keeps
// the five overviews' own filter/paging state self-contained, exactly as the standalone screens behave.

// Which tab to open on entry — lets the standalone deep-links (/dier-attendances, /entingen-overzicht …)
// land on the matching tab instead of always the first.
export type OpvangTab = "aanwezig" | "entingen" | "ontwormen" | "medicijn" | "vertrokken";

export interface OpvangOverzichtenProps {
  // openAnimalFromOverzicht in App.tsx: id (+ optional inenting id for the Bescherming deep-link). Called
  // with just an id it behaves like the plain present/placed "open animal" handler.
  onOpenAnimal: (dierId: string, inentingId?: number | null) => void;
  initialTab?: OpvangTab;
}

export function OpvangOverzichten({ onOpenAnimal, initialTab = "aanwezig" }: OpvangOverzichtenProps) {
  const t = useTranslate();
  const [tab, setTab] = useState<OpvangTab>(initialTab);

  const tabs = [
    { key: "aanwezig", label: t("opvangOverzichten.tab.aanwezig") },
    { key: "entingen", label: t("opvangOverzichten.tab.entingen") },
    { key: "ontwormen", label: t("opvangOverzichten.tab.ontwormen") },
    { key: "medicijn", label: t("opvangOverzichten.tab.medicijn") },
    { key: "vertrokken", label: t("opvangOverzichten.tab.vertrokken") },
  ];

  return (
    <div className="opvang-overzichten">
      <Tabs
        tabs={tabs}
        activeKey={tab}
        onChange={(key) => setTab(key as OpvangTab)}
        ariaLabel={t("menu.opvangOverzichten")}
      />
      <div className="opvang-overzichten__panel" role="tabpanel" aria-labelledby={`fui-tab-${tab}`}>
        {tab === "aanwezig" && <AnimalPresentView onOpenAnimal={(id) => onOpenAnimal(id)} />}
        {tab === "entingen" && <EntingenOverzicht onOpenAnimal={onOpenAnimal} />}
        {tab === "ontwormen" && <OntwormenOverzicht onOpenAnimal={onOpenAnimal} />}
        {tab === "medicijn" && (
          <MedicijnOverzicht onOpenAnimal={(id) => onOpenAnimal(id)} />
        )}
        {tab === "vertrokken" && <AnimalPlacedView onOpenAnimal={(id) => onOpenAnimal(id)} />}
      </div>
    </div>
  );
}
